/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Grid,
  GridItem,
  HStack,
  Input,
  Text,
  VStack,
  Box,
  Image,
  Checkbox,
  CheckboxGroup,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
} from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';
import { FormData, StyleField, AddOnFields } from '@/services/addProduct/types';
import {
  useGetAddOnFields,
  useGetStyleFields,
  useGetCategories,
  useGetSizes,
  useGetAddOnFieldsByCategory,
} from '@/services/addProduct';
import AddOnVariantItem from './AddOnVariantItem';
import Button from '../common/Button';
import { useStore } from '@/store/store';
import { IProductEditResponse } from '@/services/manageProduct/types';

interface CustomizeSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  clearErrors: UseFormClearErrors<FormData>;
  trigger: any;
  product?: IProductEditResponse;
  id?: string;
}

const CustomizeSection = ({
  control,
  errors,
  watch,
  setValue,
  clearErrors,
}: CustomizeSectionProps) => {
  const selectedCategoryId = watch('categorySelection.category');
  const variants = watch('variants') || [];
  const { user } = useStore();
  const { data: styleFields = [] } = useGetStyleFields();
  const { data: addOnFields = [] } = useGetAddOnFields();
  const { data: addOnFieldsWithVariants = [] } = useGetAddOnFieldsByCategory(selectedCategoryId);
  const { data: categories = [] } = useGetCategories();
  const { data: sizes = [] } = useGetSizes();

  const isCustomizable = watch('is_customizable');
  const isAddOn = watch('is_add_on');
  const addOnsData = watch('add_ons_data');

  const selectedCategory = categories.find((cat: any) => cat.id.toString() === selectedCategoryId);
  const selectedCategoryName = selectedCategory?.name || '';

  const customizationType = selectedCategoryName.toLowerCase().includes('saree')
    ? 'Blouse'
    : selectedCategoryName.toLowerCase().includes('kurta')
      ? 'Kurta Style'
      : 'Style';

  const filteredAddOnFields = addOnFields.filter(
    (addOn: AddOnFields) => addOn.category.name === selectedCategoryName && addOn.is_active,
  );

  const activeStyleFields = styleFields.filter(
    (field: StyleField) => field.category.name === selectedCategoryName && field.is_active,
  );
  const vendorCurrency = user?.currency || 'NPR'; // ← Always up-to-date
  const currencySymbol = vendorCurrency === 'NPR' ? 'Rs.' : '$';
  const hasAddOnOptions = filteredAddOnFields.length > 0;
  const hasStyleOptions = activeStyleFields.length > 0;

  // Detect if "Custom Size Only" is selected anywhere
  const hasCustomSizeOnly = variants.some((variant: any) =>
    variant.sizes?.some((size: any) => {
      const selectedSize = sizes.find((s: any) => String(s.id) === String(size.size));
      return selectedSize?.value === 'Custom Size Only';
    }),
  );

  // MAIN LOGIC: When to show the ENTIRE customization section
  const shouldShowCustomizationSection = hasStyleOptions || (!hasStyleOptions && hasCustomSizeOnly);

  // Always show add-ons if they exist
  const shouldShowAddOnsSection = hasAddOnOptions;

  // If no category selected → hide everything
  if (!selectedCategoryId) return null;

  // If neither add-ons nor customization should appear → hide entire box
  if (!shouldShowAddOnsSection && !shouldShowCustomizationSection) {
    return null;
  }

  const handleAddOnToggle = async (isChecked: boolean) => {
    if (!isChecked) {
      setValue('add_ons_data', []);
      clearErrors('add_ons_data');
    } else {
      const initialData = filteredAddOnFields.map((addOn: AddOnFields) => ({
        add_on_field: addOn.id,
        is_enabled: false,
        variants: [],
      }));
      setValue('add_ons_data', initialData);
    }
  };

  const handleAddOnEnabledChange = (filteredIndex: number, isChecked: boolean) => {
    const addOn = filteredAddOnFields[filteredIndex];
    const dataIndex = addOnsData.findIndex((item: any) => item.add_on_field === addOn.id);

    if (dataIndex !== -1) {
      const updated = [...addOnsData];
      updated[dataIndex].is_enabled = isChecked;

      if (isChecked) {
        // Find the matching add-on with variants from the category API
        const addOnWithVariants = addOnFieldsWithVariants.find(
          (aof: AddOnFields) => aof.id === addOn.id,
        );

        // Pre-populate from product_add_ons if available
        if (addOnWithVariants?.product_add_ons && addOnWithVariants.product_add_ons.length > 0) {
          updated[dataIndex].variants = addOnWithVariants.product_add_ons.map((pao: any) => ({
            display_name: pao.display_name || '',
            color: pao.color || { name: '', hex_code: '', id: pao.color?.id },
            price: String(pao.price || '0.00'),
            weight: String(pao.weight || '0.00'),
            description: pao.description || '',
            photo: pao.images?.[0]?.image || pao.images?.[0]?.image_medium || '',
            photoFile: undefined,
          }));
        } else if (updated[dataIndex].variants.length === 0) {
          // Add empty variant if no product_add_ons
          updated[dataIndex].variants.push({
            display_name: '',
            color: { name: '', hex_code: '' },
            price: '0.00',
            weight: '0.00',
            description: '',
            photo: '',
            photoFile: undefined,
            isModified: true,
          });
        }
      } else {
        updated[dataIndex].variants = [];
      }

      setValue('add_ons_data', updated);
      clearErrors('add_ons_data');
    }
  };

  const handleCustomizeToggle = (isChecked: boolean) => {
    if (!isChecked) {
      setValue('measurement_fields', []);
      setValue('customized_price', '0');
      setValue('customized_weight', '0.00');
      clearErrors(['measurement_fields', 'customized_price', 'customized_weight']);
    }
  };

  return (
    <Flex
      gap={5}
      direction="column"
      bg="background.secondary"
      p={4}
      borderWidth="1px"
      borderRadius="md"
    >
      <Text fontWeight="semibold" fontSize="lg">
        Customize Size
      </Text>

      <Grid gap={8}>
        {shouldShowAddOnsSection && (
          <GridItem>
            <FormControl display="flex" alignItems="center">
              <Controller
                name="is_add_on"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="add-ons-toggle"
                    isChecked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      handleAddOnToggle(e.target.checked);
                    }}
                    colorScheme="brand.primary"
                    mr={2}
                  />
                )}
              />
              <FormLabel htmlFor="add-ons-toggle" mb={0}>
                Select from a variety of add-ons to personalize your product
              </FormLabel>
            </FormControl>

            {isAddOn && (
              <VStack spacing={4} align="stretch" mt={4}>
                {filteredAddOnFields.map((addOn: AddOnFields, addOnIdx: number) => {
                  const dataIndex = addOnsData.findIndex((i: any) => i.add_on_field === addOn.id);
                  const variants = addOnsData[dataIndex]?.variants || [];
                  const isEnabled = addOnsData[dataIndex]?.is_enabled || false;

                  // Get errors for this add-on group
                  const addOnErrors = errors.add_ons_data?.[dataIndex];

                  return (
                    <Box
                      key={addOn.id}
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      bg="gray.50"
                    >
                      <HStack justify="space-between" spacing={3}>
                        <Checkbox
                          isChecked={isEnabled}
                          onChange={(e) => handleAddOnEnabledChange(addOnIdx, e.target.checked)}
                          colorScheme="brand.primary"
                          size="md"
                          spacing={2}
                        >
                          <Text fontWeight="medium" fontSize="sm">
                            {addOn.name}
                          </Text>
                        </Checkbox>
                      </HStack>

                      {/* Show group-level errors */}
                      {isAddOn && addOnErrors && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          {addOnErrors.message ||
                            (addOnErrors.variants &&
                              !isEnabled &&
                              'Please select at least one add-ons') ||
                            addOnErrors.variants?.message}
                        </Text>
                      )}

                      {isAddOn &&
                        errors.add_ons_data &&
                        !addOnErrors &&
                        errors.add_ons_data.message && (
                          <Text color="red.500" fontSize="sm" mt={2}>
                            {errors.add_ons_data.message}
                          </Text>
                        )}

                      {isEnabled && (
                        <>
                          {/* Variant list */}
                          <VStack spacing={5} align="stretch" mb={3} w="full">
                            {variants.map((_: any, variantIdx: number) => (
                              <Box key={variantIdx} w="full">
                                <AddOnVariantItem
                                  control={control}
                                  errors={errors}
                                  addOnIndex={dataIndex}
                                  variantIndex={variantIdx}
                                  removeVariant={() => {
                                    const updated = [...addOnsData];
                                    updated[dataIndex].variants.splice(variantIdx, 1);
                                    setValue('add_ons_data', updated);
                                  }}
                                  currencySymbol={currencySymbol}
                                  setValue={setValue}
                                />
                              </Box>
                            ))}

                            {addOnErrors?.variants && !Array.isArray(addOnErrors.variants) && (
                              <Text color="red.500" fontSize="sm">
                                {addOnErrors.variants.message}
                              </Text>
                            )}
                          </VStack>

                          <Button
                            onClick={() => {
                              const updated = [...addOnsData];
                              // Check if there are existing variants to determine isPrimary

                              updated[dataIndex].variants.push({
                                display_name: '',
                                color: { name: '', hex_code: '', id: undefined },
                                price: '',
                                weight: '',
                                description: '',
                                photo: '',
                                photoFile: undefined,
                              });
                              setValue('add_ons_data', updated);
                            }}
                          >
                            + Add Variant
                          </Button>
                        </>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            )}
          </GridItem>
        )}

        {shouldShowCustomizationSection && (
          <GridItem>
            <FormControl display="flex" alignItems="center">
              <Controller
                name="is_customizable"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="customize-style"
                    isChecked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      handleCustomizeToggle(e.target.checked);
                    }}
                    colorScheme="brand.primary"
                    mr={2}
                  />
                )}
              />
              <FormLabel htmlFor="customize-style" mb="0">
                Enable the option to customize and select your {customizationType.toLowerCase()} for
                a perfect match.
              </FormLabel>
            </FormControl>

            {isCustomizable && (
              <VStack spacing={4} mt={4} align="stretch">
                <HStack
                  spacing={6}
                  direction={{ base: 'column', md: 'row' }}
                  align="start"
                  w="full"
                >
                  <FormControl
                    isInvalid={!!errors.customized_price}
                    width={hasStyleOptions ? 'auto' : { base: '100%', md: '50%' }}
                    flex={hasStyleOptions ? '1' : undefined}
                  >
                    <FormLabel>
                      Customize {customizationType} Price <Asterisk />
                    </FormLabel>
                    <Box position="relative">
                      <Text
                        position="absolute"
                        left="10px"
                        top="50%"
                        transform="translateY(-50%)"
                        color="text.primary"
                        pointerEvents="none"
                        zIndex={10}
                      >
                        {currencySymbol}
                      </Text>
                      <Controller
                        name="customized_price"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            placeholder="0.00"
                            bg="white"
                            pl="30px"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            onFocus={() =>
                              (field.value === '0' || field.value === '0.00') && field.onChange('')
                            }
                          />
                        )}
                      />
                    </Box>
                    <FormErrorMessage>{errors.customized_price?.message}</FormErrorMessage>
                  </FormControl>

                  {hasStyleOptions && (
                    <FormControl isInvalid={!!errors.customized_weight} flex="1">
                      <FormLabel>
                        {customizationType} Weight (kg) <Asterisk />
                      </FormLabel>
                      <Controller
                        name="customized_weight"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            placeholder="0.00"
                            bg="white"
                            value={field.value || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v.includes('.') && v.split('.')[1].length > 2) return;
                              field.onChange(v);
                            }}
                            onFocus={() => field.value === '0.00' && field.onChange('')}
                            step="0.01"
                          />
                        )}
                      />
                      <FormErrorMessage>{errors.customized_weight?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                </HStack>

                {hasStyleOptions && (
                  <>
                    <Text color="text.secondary">Select {customizationType} Design Options</Text>
                    <FormControl isInvalid={!!errors.measurement_fields}>
                      <Controller
                        name="measurement_fields"
                        control={control}
                        render={({ field }) => (
                          <CheckboxGroup
                            value={field.value?.map(String) || []}
                            onChange={(vals) => {
                              const nums = vals.map(Number).filter((n) => !isNaN(n));
                              field.onChange(nums);
                              if (nums.length > 0) clearErrors('measurement_fields');
                            }}
                          >
                            <Grid
                              templateColumns={{
                                base: '1fr',
                                md: 'repeat(2, 1fr)',
                                lg: 'repeat(3, 1fr)',
                              }}
                              gap={4}
                            >
                              {activeStyleFields.map((option: StyleField) => (
                                <Checkbox
                                  key={option.id}
                                  value={String(option.id)}
                                  colorScheme="brand.primary"
                                >
                                  <HStack spacing={2}>
                                    {option.image_small && (
                                      <Image
                                        src={option.image_small}
                                        boxSize="20px"
                                        borderRadius="md"
                                      />
                                    )}
                                    <Text>{option.name}</Text>
                                  </HStack>
                                </Checkbox>
                              ))}
                            </Grid>
                          </CheckboxGroup>
                        )}
                      />
                      <FormErrorMessage>{errors.measurement_fields?.message}</FormErrorMessage>
                    </FormControl>
                  </>
                )}
              </VStack>
            )}
          </GridItem>
        )}
      </Grid>
    </Flex>
  );
};

export default CustomizeSection;
